import React from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { getShopUrl } from "shared-frontend/functions/products";
import { Link } from "@yoast/ui-library";

import { messages } from "./messages";
import * as styles from "./styles.module.scss";

/**
 * Whether to offer a renewal rather than a new purchase.
 *
 * `renewalUrl` is null whenever the server decided this subscription cannot be renewed — including the
 * case where it has no subscription number to build a renewal link from. Requiring it here is what keeps
 * the affordance honest: the previous version rendered a renew button with `href={null}`, which looks
 * live and goes nowhere.
 *
 * @param {Object}    subscription        The subscription.
 * @param {boolean}   subscriptionIsMonthly Whether it is a monthly subscription.
 * @param {Date|null} date                The end date, or the next payment date when there is no end date.
 *
 * @returns {boolean} Whether renewal is on offer.
 */
function canBeRenewed( subscription, subscriptionIsMonthly, date ) {
	if ( ! subscription.renewalUrl ) {
		return false;
	}

	if ( [ "active", "on-hold" ].includes( subscription.status ) ) {
		return true;
	}

	// A lapsed subscription can still be renewed for a month, but a monthly one is bought again instead.
	const OneMonthAgo = new Date();
	OneMonthAgo.setMonth( OneMonthAgo.getMonth() - 1 );

	return ! subscriptionIsMonthly &&
		subscription.status === "expired" &&
		date !== null &&
		date.getTime() > OneMonthAgo.getTime();
}

const SubscriptionDetails = ( props ) => {
	const subscription = props.subscription;

	if ( subscription.status === "on-hold" ) {
		const clickHandler = () => props.showDetailsModal( subscription );

		return <>
			<div className={ styles.error }>{ props.intl.formatMessage( messages.paymentFailed ) }</div>
			<Link as="button" onClick={ clickHandler }>{ props.intl.formatMessage( messages.seeDetails ) }</Link>
		</>;
	}

	const goToYoastSubscriptionsPage = getShopUrl() + "/subscriptions";
	const subscriptionIsMonthly = subscription.name.includes( "(monthly)" );
	const date = subscription.hasEndDate ? subscription.endDate : subscription.nextPayment;
	const statuses = [ "expired", "cancelled", "refunded" ];
	const expired = statuses.includes( subscription.status ) ? "Expired" : "Expires";

	let linkText, linkClass, linkTo;
	if ( canBeRenewed( subscription, subscriptionIsMonthly, date ) ) {
		linkTo = subscription.renewalUrl;
		linkText = props.intl.formatMessage( messages.manualRenewMessage );
		linkClass = "renew_now_button_subs_page";
	} else {
		// Both buy-new targets are now the one shop URL: the multi-shop era is over, so the cart builder's
		// shop-id branching described a topology that no longer exists.
		linkTo = subscriptionIsMonthly ? goToYoastSubscriptionsPage : getShopUrl() + "/shop";
		linkText = props.intl.formatMessage( messages.buyNewMessage );
		linkClass = "buy_subscription_button_subs_page";
	}

	return (
		<>
			<div className={ styles.error }>
				{ /* A subscription can legitimately have neither date — a pending-cancel row has its
					nextPayment nulled, and an inactive one may have no end date — and this component renders for
					inactive rows too. The date line is dropped rather than rendered as "Expired Invalid Date". */ }
				{ date !== null && <FormattedMessage
					id="details.redMessageText"
					defaultMessage="{ expired } { date }"
					values={ {
						expired: expired,
						date: date.toLocaleDateString( "en-US", {
							year: "numeric",
							month: "short",
							day: "numeric",
						} ),
					} }
				/> }
			</div>
			<Link href={ linkTo } className={ linkClass }>{ linkText }</Link>
		</>
	);
};


export default injectIntl( SubscriptionDetails );
